import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import type { PaymentCallbackEvent, PaymentModalProps } from '../types/payment.types'

export interface PaymentResult {
  sourceId?: string
  sourceType?: string
  status: 'success' | 'failed' | 'processing'
  message?: string
  orderId?: string
  data?: Record<string, unknown>
  timestamp: number
}

export const usePaymentUIStore = defineStore('payment-ui', () => {
  // Modal state
  const showPaymentModal = ref(false)
  const paymentModalProps = ref<PaymentModalProps | null>(null)
  const onPaymentSuccess = ref<((data: PaymentCallbackEvent) => void) | null>(null)
  const onOrderCreated = ref<((data: { orderId: string; sourceId: string }) => Promise<void>) | null>(null)

  // Payment result state - for components to watch
  const paymentResult = ref<PaymentResult | null>(null)

  // Computed
  const isModalVisible = computed(() => showPaymentModal.value)
  const hasModalProps = computed(() => !!paymentModalProps.value)
  const hasPaymentResult = computed(() => !!paymentResult.value)
  const isPaymentSuccess = computed(() => paymentResult.value?.status === 'success')
  const isPaymentFailed = computed(() => paymentResult.value?.status === 'failed')

  // Actions
  const openPaymentModal = (props: PaymentModalProps) => {
    paymentModalProps.value = props
    showPaymentModal.value = true
    onPaymentSuccess.value = props.onPaymentSuccess || null
    onOrderCreated.value = props.onOrderCreated || null
    // Clear previous result when opening new payment
    paymentResult.value = null
  }

  const closePaymentModal = () => {
    showPaymentModal.value = false
    paymentModalProps.value = null
  }

  const setPaymentResult = (event: PaymentCallbackEvent) => {
    const result: PaymentResult = {
      sourceId: paymentModalProps.value?.sourceId,
      sourceType: paymentModalProps.value?.sourceType,
      status: event.type === 'success' ? 'success' : 'failed',
      message: event.message,
      orderId: event.data ? (event.data.orderId as string) : undefined,
      data: event.data,
      timestamp: Date.now(),
    }

    paymentResult.value = result
  }

  const clearPaymentResult = () => {
    paymentResult.value = null
  }

  const resetPaymentState = () => {
    showPaymentModal.value = false
    paymentModalProps.value = null
    paymentResult.value = null
  }

  return {
    // State
    showPaymentModal,
    paymentModalProps,
    paymentResult,
    onPaymentSuccess,
    onOrderCreated,

    // Computed
    isModalVisible,
    hasModalProps,
    hasPaymentResult,
    isPaymentSuccess,
    isPaymentFailed,

    // Actions
    openPaymentModal,
    closePaymentModal,
    setPaymentResult,
    clearPaymentResult,
    resetPaymentState,
  }
})
